import { useState } from 'react';

import { DEOnly, UKOnly } from '@iwIntlProvider/CountryWrappers';
import { Button, Modal } from '@iwoca/orion';
import { EditSVG } from '@svgComponents/editSVG';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import styles from './BankAccountCard.module.css';
import { getCountry } from '../../../iwIntlProvider/CountryProvider';
import cardStyles from '../../../styles/variables/cards.module.css';

export function BankAccountCard({
    accountNumber,
    sortCode,
    updateBankAccount
}: {
    accountNumber: string;
    sortCode: string;
    isFundingDisabled: boolean;
    updateBankAccount: () => void;
}) {
    const [showUpdateBankAccountModal, setShowUpdateBankAccountModal] =
        useState(false);

    const bankAccountCardStyles = classNames(
        styles.BankAccountCard,
        cardStyles.card
    );

    const showUpdateBankAccountButton = getCountry() === 'UK';

    return (
        <>
            <Modal
                title="Update your bank account"
                subTitle="This will open a form to add new bank details."
                isOpen={showUpdateBankAccountModal}
                onClose={() => setShowUpdateBankAccountModal(false)}
                closeIcon
            >
                <div className="flex justify-end">
                    <Button
                        variant="tertiary"
                        onClick={() => setShowUpdateBankAccountModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="action"
                        onClick={() => {
                            updateBankAccount?.();
                            setShowUpdateBankAccountModal(false);
                        }}
                    >
                        Update
                    </Button>
                </div>
            </Modal>
            <div className={bankAccountCardStyles}>
                <div className={styles.content}>
                    <div className={styles.description}>
                        <div className={styles.type}>
                            <FormattedMessage
                                id="_.your_company.bank_account_card.bank_account"
                                defaultMessage="Your bank account"
                            />
                            {showUpdateBankAccountButton && (
                                <button
                                    className="border-none bg-transparent fill-primary-10 stroke-primary-10 hover:cursor-pointer"
                                    onClick={() =>
                                        setShowUpdateBankAccountModal(true)
                                    }
                                    data-testid="update-bank-account"
                                >
                                    <EditSVG className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className={styles.bankAccountInfoText}>
                        <FormattedMessage
                            id="_.your_company.bank_account_card.bank_account_info_text"
                            defaultMessage="We'll be sending your funds to:"
                        />
                    </div>

                    <BankDetails
                        accountNumber={accountNumber}
                        sortCode={sortCode}
                    />

                    <DEOnly>
                        <div className={styles.changeAccountDetails}>
                            <p>
                                Um Ihre Kontodaten zu ändern, melden Sie sich
                                bitte telefonisch bei uns: 069 / 94323020
                            </p>
                        </div>
                    </DEOnly>
                </div>
            </div>
        </>
    );
}

function BankDetails({
    accountNumber,
    sortCode
}: {
    accountNumber: string;
    sortCode?: string;
}) {
    return (
        <>
            <DEOnly>
                <div className={styles.accountNumber} data-testid="bank-iban">
                    <FormattedMessage
                        id="_.your_company.bank_account_card.iban"
                        defaultMessage="IBAN:"
                    />{' '}
                    <span className="fs-exclude">{accountNumber}</span>
                </div>
            </DEOnly>
            <UKOnly>
                <>
                    <div
                        className={styles.accountNumber}
                        data-testid="bank-account-number"
                    >
                        <FormattedMessage
                            id="_.your_company.bank_account_card.bank_account_number"
                            defaultMessage="Account number:"
                        />{' '}
                        <span className="fs-exclude">{accountNumber}</span>
                    </div>
                    <div
                        className={styles.sortCode}
                        data-testid="bank-sort-code"
                    >
                        <FormattedMessage
                            id="_.your_company.bank_account_card.sort_code"
                            defaultMessage="Sort code:"
                        />{' '}
                        <span className="fs-exclude">{sortCode}</span>
                    </div>
                </>
            </UKOnly>
        </>
    );
}
